import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  TrendingUp, DollarSign, AlertTriangle, Download, Calendar, 
  PieChart as PieChartIcon, BarChart3, FileSpreadsheet, Users,
  UserCheck, Award, Shield, Activity, Plus, Trash2, Minus, TrendingDown, Settings
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, ComposedChart, Line, Legend } from "recharts";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const DEFAULT_TARGET = 500;

const EXPENSE_CATEGORIES = ["Rent", "Equipment", "Insurance/Admin", "Marketing", "Other Expenses"];

const EXPENSE_CAT_COLORS = {
  "Rent": "#ef4444",
  "Equipment": "#3b82f6",
  "Insurance/Admin": "#f97316",
  "Marketing": "#a855f7",
  "Other Expenses": "#6b7280",
};

const CATEGORY_COLORS = {
  group_class: "#3b82f6",
  private: "#22c55e",
  grading: "#a855f7",
  membership: "#f97316",
  tai_chi: "#14b8a6",
};

const CATEGORY_LABELS = {
  group_class: "Monthly Group Class",
  private: "Private Lessons",
  grading: "Grading Fees",
  membership: "Membership & Insurance",
  tai_chi: "Tai Chi",
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const INITIALS_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6",
  "#3b82f6", "#6366f1", "#a855f7", "#ec4899", "#f43f5e",
  "#0ea5e9", "#8b5cf6", "#d946ef", "#84cc16", "#06b6d4",
];

function getInitialsColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return INITIALS_COLORS[Math.abs(hash) % INITIALS_COLORS.length];
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0].substring(0, 2).toUpperCase();
}

function StudentAvatar({ name }) {
  const bg = getInitialsColor(name || "");
  return (
    <div className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-white shrink-0 text-[10px]"
      style={{ backgroundColor: bg }} title={name}>
      {getInitials(name)}
    </div>
  );
}

function RevenueGauge({ value, target }) {
  const pct = Math.min(value / target, 1.5);
  const displayPct = Math.min(pct * 100, 150);
  const color = pct >= 1 ? "#22c55e" : pct >= 0.6 ? "#eab308" : "#ef4444";
  const label = pct >= 1 ? "TARGET HIT" : pct >= 0.6 ? "ON TRACK" : "BEHIND";

  // SVG arc gauge - semicircle from left to right
  const cx = 80, cy = 75, r = 60, stroke = 12;
  const circumference = Math.PI * r; // half-circle
  const filledLen = Math.min(pct, 1) * circumference;
  const emptyLen = circumference - filledLen;

  // Needle angle: 0% = 180deg (left), 100% = 0deg (right)
  const needleAngle = 180 - Math.min(pct, 1) * 180;
  const needleRad = (needleAngle * Math.PI) / 180;
  const needleLen = r - 8;
  const nx = cx + needleLen * Math.cos(needleRad);
  const ny = cy - needleLen * Math.sin(needleRad);

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: 160, height: 90 }}>
        <svg width="160" height="90" viewBox="0 0 160 90">
          {/* Background arc */}
          <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#27272a" strokeWidth={stroke} strokeLinecap="round" />
          {/* Filled arc */}
          <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={`${filledLen} ${emptyLen}`} />
          {/* Needle */}
          <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          <circle cx={cx} cy={cy} r="4" fill="#fff" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-0">
          <p className="text-lg font-bold text-white leading-none">£{value.toFixed(0)}</p>
          <p className="text-[9px] text-[#A1A1AA]">of £{target}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-[10px] font-semibold tracking-wide" style={{ color }}>{label}</span>
        <span className="text-[10px] text-[#A1A1AA]">({displayPct.toFixed(0)}%)</span>
      </div>
    </div>
  );
}

export default function BillingReports() {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [feePayments, setFeePayments] = useState([]);
  const [privateSessions, setPrivateSessions] = useState([]);
  const [groupPayments, setGroupPayments] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [categorySums, setCategorySums] = useState({});
  
  // v12.7.2: Drill-down dialog for category totals
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownCategory, setDrilldownCategory] = useState(null);
  const [drilldownTitle, setDrilldownTitle] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ category: "Rent", amount: "", description: "", date: new Date().toISOString().split("T")[0] });
  const [savingExpense, setSavingExpense] = useState(false);
  const [monthlyTarget, setMonthlyTarget] = useState(() => {
    try {
      const saved = localStorage.getItem('loongfu_monthly_target');
      const parsed = saved !== null ? Number(saved) : NaN;
      return isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_TARGET;
    } catch { return DEFAULT_TARGET; }
  });
  const [performanceMonths, setPerformanceMonths] = useState("6");
  const [incExpMonths, setIncExpMonths] = useState("6");

  // Sync monthly target from settings API on load
  useEffect(() => {
    axios.get(`${API}/settings/monthly_target`).then(res => {
      if (res.data?.value) {
        const val = Number(res.data.value);
        if (isFinite(val) && val > 0) {
          setMonthlyTarget(val);
          try { localStorage.setItem('loongfu_monthly_target', String(val)); } catch {}
        }
      }
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [studentsRes, feePaymentsRes, privateSessionsRes, paymentsRes, attendanceRes, expensesRes, categorySumsRes] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(`${API}/fee-payments`).catch(() => ({ data: [] })),
        axios.get(`${API}/private-sessions`).catch(() => ({ data: [] })),
        axios.get(`${API}/payments/unified`).catch(() => ({ data: [] })),
        axios.get(`${API}/attendance`).catch(() => ({ data: [] })),
        axios.get(`${API}/expenses`).catch(() => ({ data: [] })),
        axios.get(`${API}/payments/category-sums`).catch(() => ({ data: {} })),
      ]);
      setStudents(studentsRes.data || []);
      setFeePayments(feePaymentsRes.data || []);
      setPrivateSessions(privateSessionsRes.data || []);
      setGroupPayments(paymentsRes.data || []);
      setAttendance(attendanceRes.data || []);
      setExpenses(expensesRes.data || []);
      setCategorySums(categorySumsRes.data || {});
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load financial data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddExpense = async () => {
    if (!expenseForm.amount || !expenseForm.date) { toast.error("Amount and date required"); return; }
    setSavingExpense(true);
    try {
      const res = await axios.post(`${API}/expenses`, { ...expenseForm, amount: parseFloat(expenseForm.amount) });
      setExpenses(prev => [res.data, ...prev]);
      setExpenseForm({ category: "Rent", amount: "", description: "", date: new Date().toISOString().split("T")[0] });
      setShowExpenseForm(false);
      toast.success("Expense logged");
    } catch (e) {
      toast.error("Failed to log expense");
    } finally { setSavingExpense(false); }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await axios.delete(`${API}/expenses/${id}`);
      setExpenses(prev => prev.filter(e => e.id !== id));
      toast.success("Expense deleted");
    } catch (e) { toast.error("Failed to delete expense"); }
  };

  const calculatePrice = (type, startTime, endTime) => {
    const start = new Date(`2000-01-01 ${startTime}`);
    const end = new Date(`2000-01-01 ${endTime}`);
    const durationMins = (end - start) / (1000 * 60);
    const duration = durationMins >= 75 ? 90 : 60;
    if (type === "Online") return duration === 90 ? 75 : 50;
    return duration === 90 ? 90 : 60;
  };

  const getStudentById = (id) => students.find(s => s.id === id);

  const financialData = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const monthlyTotals = Array(12).fill(0).map((_, i) => ({ month: MONTH_NAMES[i], total: 0, group_class: 0, private: 0, grading: 0, membership: 0, tai_chi: 0 }));
    let yearToDate = 0, monthlyTotal = 0, outstanding = 0;
    const categoryTotals = { group_class: 0, private: 0, grading: 0, membership: 0, tai_chi: 0 };
    const allRecords = [];
    
    // v12.8.1: Track legacy IDs already counted via unified to prevent double-counting
    const unifiedLegacyIds = new Set();
    
    // groupPayments now comes from /payments/unified (includes legacy records)
    groupPayments.forEach(payment => {
      // Track legacy IDs to skip them in feePayments loop
      if (payment._legacy) unifiedLegacyIds.add(payment.id);
      
      if (payment.status !== "paid" && !payment._legacy) { outstanding += parseFloat(payment.amount) || 0; return; }
      const py = payment.year || new Date(payment.date || payment.created_at || 0).getFullYear();
      const pm = (payment.month ? payment.month - 1 : new Date(payment.date || payment.created_at || 0).getMonth());
      const amount = parseFloat(payment.amount) || 0;
      
      // Map unified category to internal key
      const cat = payment.category || 'Lesson';
      const catKey = cat === 'Membership' ? 'membership' : cat === 'Grading' ? 'grading' : 'group_class';
      
      allRecords.push({ date: payment.payment_date || payment.date || `${py}-${String((pm||0)+1).padStart(2, '0')}-01`, student_id: payment.student_id, category: catKey, amount, payment_method: payment.payment_method, status: payment.status, notes: payment.notes, _legacy: payment._legacy });
      if (py === selectedYear) { categoryTotals[catKey] += amount; if (monthlyTotals[pm]) { monthlyTotals[pm][catKey] += amount; monthlyTotals[pm].total += amount; } if (pm <= currentMonth || selectedYear < currentYear) yearToDate += amount; }
      if (py === currentYear && pm === currentMonth) monthlyTotal += amount;
    });

    // feePayments: Only count records NOT already in unified (non-membership/grading legacy)
    feePayments.forEach(payment => {
      if (unifiedLegacyIds.has(payment.id)) return; // Already counted via unified
      const paymentDate = new Date(payment.date);
      const amount = parseFloat(payment.amount) || 0;
      const pm = paymentDate.getMonth();
      const py = paymentDate.getFullYear();
      allRecords.push({ date: payment.date, student_id: payment.student_id, category: payment.category, amount, payment_method: payment.payment_method, status: "Paid", notes: payment.notes });
      if (py === selectedYear) { categoryTotals[payment.category] = (categoryTotals[payment.category] || 0) + amount; if (monthlyTotals[pm]) { monthlyTotals[pm][payment.category] += amount; monthlyTotals[pm].total += amount; } if (pm <= currentMonth || selectedYear < currentYear) yearToDate += amount; }
      if (py === currentYear && pm === currentMonth) monthlyTotal += amount;
    });

    privateSessions.forEach(session => {
      const sd = new Date(session.session_date);
      const price = session.price != null ? parseFloat(session.price) : calculatePrice(session.type, session.start_time, session.end_time);
      const sm = sd.getMonth();
      const sy = sd.getFullYear();
      if (session.status === "No-Charge") return;
      allRecords.push({ date: session.session_date, student_id: session.student_id, category: "private", amount: price, payment_method: session.payment_method, status: session.payment_status, notes: session.sifu_notes });
      if (session.payment_status === "Pending") outstanding += price;
      if (sy === selectedYear && session.payment_status === "Paid") { categoryTotals.private += price; monthlyTotals[sm].private += price; monthlyTotals[sm].total += price; if (sm <= currentMonth || selectedYear < currentYear) yearToDate += price; }
      if (sy === currentYear && sm === currentMonth && session.payment_status === "Paid") monthlyTotal += price;
    });

    // groupPayments already processed above with proper category mapping

    const monthlyAttendance = Array(12).fill(0);
    attendance.forEach(record => {
      let d;
      if (record.date && record.date.includes('/')) { const parts = record.date.split('/'); d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`); }
      else { d = new Date(record.date || record.checked_in_at); }
      if (!isNaN(d) && d.getFullYear() === selectedYear) monthlyAttendance[d.getMonth()]++;
    });

    // Expenses
    let monthlyExpenses = 0, yearExpenses = 0;
    const monthlyExpenseTotals = Array(12).fill(0);
    expenses.forEach(exp => {
      const d = new Date(exp.date);
      const amount = parseFloat(exp.amount) || 0;
      if (!isNaN(d) && d.getFullYear() === selectedYear) {
        monthlyExpenseTotals[d.getMonth()] += amount;
        if (d.getMonth() <= currentMonth || selectedYear < currentYear) yearExpenses += amount;
      }
      if (!isNaN(d) && d.getFullYear() === currentYear && d.getMonth() === currentMonth) monthlyExpenses += amount;
    });

    const pieData = Object.entries(categoryTotals).filter(([_, v]) => v > 0).map(([k, v]) => ({ name: CATEGORY_LABELS[k], value: v, color: CATEGORY_COLORS[k] }));

    const lastNMonths = [];
    const nMonths = parseInt(performanceMonths);
    for (let i = nMonths - 1; i >= 0; i--) {
      const mi = (currentMonth - i + 12) % 12;
      const yo = currentMonth - i < 0 ? -1 : 0;
      const y = currentYear + yo;
      lastNMonths.push(y === selectedYear ? monthlyTotals[mi] : { month: MONTH_NAMES[mi], total: 0, group_class: 0, private: 0, grading: 0, membership: 0, tai_chi: 0 });
    }

    // Income vs Expense (togglable months)
    const incomeVsExpenseData = [];
    const incExpN = parseInt(incExpMonths);
    for (let i = incExpN - 1; i >= 0; i--) {
      const mi = (currentMonth - i + 12) % 12;
      const yo = currentMonth - i < 0 ? -1 : 0;
      const y = currentYear + yo;
      incomeVsExpenseData.push({
        month: MONTH_NAMES[mi],
        Income: y === selectedYear ? monthlyTotals[mi].total : 0,
        Expenses: y === selectedYear ? monthlyExpenseTotals[mi] : 0,
      });
    }

    // Expense distribution by category
    const expenseCategoryTotals = {};
    expenses.forEach(exp => {
      const d = new Date(exp.date);
      if (!isNaN(d) && d.getFullYear() === selectedYear) {
        const cat = exp.category || "Other Expenses";
        expenseCategoryTotals[cat] = (expenseCategoryTotals[cat] || 0) + (parseFloat(exp.amount) || 0);
      }
    });
    const expenseDistribution = Object.entries(expenseCategoryTotals)
      .filter(([_, v]) => v > 0)
      .map(([k, v]) => ({ name: k, value: v, color: EXPENSE_CAT_COLORS[k] || "#6b7280" }));

    const attendanceVsRevenue = Array(12).fill(null).map((_, i) => ({ month: MONTH_NAMES[i], revenue: monthlyTotals[i].total, attendance: monthlyAttendance[i] }));

    const outstandingRecords = [];
    privateSessions.filter(s => s.payment_status === "Pending" && s.status !== "No-Charge").forEach(s => { const student = getStudentById(s.student_id); outstandingRecords.push({ name: student?.name || "Unknown", amount: s.price != null ? parseFloat(s.price) : calculatePrice(s.type, s.start_time, s.end_time), type: "Private" }); });
    groupPayments.filter(p => p.status !== "paid").forEach(p => { const student = getStudentById(p.student_id); outstandingRecords.push({ name: student?.name || "Unknown", amount: parseFloat(p.amount) || 0, type: "Group" }); });

    const netProfitMonthly = monthlyTotal - monthlyExpenses;
    const netProfitYTD = yearToDate - yearExpenses;

    return {
      monthlyTotal, yearToDate, outstanding, categoryTotals, pieData,
      monthlyTotals, lastNMonths, allRecords, attendanceVsRevenue, outstandingRecords,
      totalRevenue: Object.values(categoryTotals).reduce((a, b) => a + b, 0),
      monthlyExpenses, yearExpenses, netProfitMonthly, netProfitYTD,
      incomeVsExpenseData, expenseDistribution,
    };
  }, [feePayments, privateSessions, groupPayments, attendance, expenses, students, selectedYear, performanceMonths, incExpMonths]);

  const exportMasterXLSX = () => {
    const XLSX = require('xlsx');

    const studentMap = {};
    students.forEach(s => { studentMap[s.id] = s; });

    const getStudentClass = (studentId) => {
      const s = studentMap[studentId];
      if (!s) return 'Unknown';
      const classes = s.class_types?.length ? s.class_types : (s.class_type ? [s.class_type] : []);
      return classes.join(', ');
    };

    // Filter records by student class type
    const isClass = (record, ...classNames) => {
      const s = studentMap[record.student_id];
      if (!s) return false;
      const classes = [...(s.class_types || []), s.class_type || ''];
      return classes.some(c => classNames.some(cn => c.toLowerCase().includes(cn.toLowerCase())));
    };

    const allSorted = financialData.allRecords
      .filter(r => new Date(r.date).getFullYear() === selectedYear)
      .sort((a, b) => b.date.localeCompare(a.date));

    const adultsRecords = allSorted.filter(r => isClass(r, 'Adults Kung Fu'));
    const dragonsRecords = allSorted.filter(r => isClass(r, 'Dragons'));
    const tigersRecords = allSorted.filter(r => isClass(r, 'Tigers'));
    const taiChiRecords = allSorted.filter(r => r.category === 'tai_chi' || isClass(r, 'Tai Chi'));
    const privateRemoteRecords = allSorted.filter(r => r.category === 'private' || isClass(r, 'Private Lessons', 'Private Only', 'Remote/International', 'International'));

    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
    };

    const buildRows = (records) => records.map(r => ({
      'Date': formatDate(r.date),
      'Student': studentMap[r.student_id]?.name || 'Unknown',
      'Category': CATEGORY_LABELS[r.category] || r.category,
      'Amount (£)': parseFloat(r.amount.toFixed(2)),
      'Payment Method': r.payment_method || '',
      'Status': r.status || '',
      'Notes': r.notes || '',
    }));

    const totalIncome = allSorted.filter(r => r.status === 'Paid' || r.status === 'paid').reduce((sum, r) => sum + r.amount, 0);
    const totalLessons = allSorted.length;

    const buildSheet = (title, records) => {
      const rows = buildRows(records);
      const tabIncome = records.filter(r => r.status === 'Paid' || r.status === 'paid').reduce((sum, r) => sum + r.amount, 0);
      const headerRows = [
        { 'Date': `Total Lessons: ${records.length}`, 'Student': '', 'Category': '', 'Amount (£)': '', 'Payment Method': '', 'Status': '', 'Notes': '' },
        { 'Date': `Total Income: £${tabIncome.toFixed(2)}`, 'Student': '', 'Category': '', 'Amount (£)': '', 'Payment Method': '', 'Status': '', 'Notes': '' },
        { 'Date': '', 'Student': '', 'Category': '', 'Amount (£)': '', 'Payment Method': '', 'Status': '', 'Notes': '' },
      ];
      const ws = XLSX.utils.json_to_sheet([...headerRows, ...rows]);
      // Auto-fit columns
      const colWidths = [14, 25, 18, 12, 18, 10, 30];
      ws['!cols'] = colWidths.map(w => ({ wch: w }));
      return ws;
    };

    // Summary tab
    const summaryData = [
      { 'Metric': `LOONG FU TONG — Financial Summary ${selectedYear}`, 'Value': '' },
      { 'Metric': '', 'Value': '' },
      { 'Metric': 'Total Lessons Taught', 'Value': totalLessons },
      { 'Metric': 'Total Income for Year', 'Value': `£${totalIncome.toFixed(2)}` },
      { 'Metric': '', 'Value': '' },
      { 'Metric': '--- Breakdown by Category ---', 'Value': '' },
      { 'Metric': 'Group Classes', 'Value': `£${(financialData.categoryTotals.group_class || 0).toFixed(2)}` },
      { 'Metric': 'Private & Remote', 'Value': `£${(financialData.categoryTotals.private || 0).toFixed(2)}` },
      { 'Metric': 'Grading Fees', 'Value': `£${(financialData.categoryTotals.grading || 0).toFixed(2)}` },
      { 'Metric': 'Membership Fees', 'Value': `£${(financialData.categoryTotals.membership || 0).toFixed(2)}` },
      { 'Metric': 'Tai Chi', 'Value': `£${(financialData.categoryTotals.tai_chi || 0).toFixed(2)}` },
      { 'Metric': '', 'Value': '' },
      { 'Metric': '--- Breakdown by Class ---', 'Value': '' },
      { 'Metric': 'Adults Kung Fu', 'Value': `${adultsRecords.length} records — £${adultsRecords.filter(r => r.status === 'Paid' || r.status === 'paid').reduce((s,r) => s+r.amount, 0).toFixed(2)}` },
      { 'Metric': 'Dragons', 'Value': `${dragonsRecords.length} records — £${dragonsRecords.filter(r => r.status === 'Paid' || r.status === 'paid').reduce((s,r) => s+r.amount, 0).toFixed(2)}` },
      { 'Metric': 'Tigers', 'Value': `${tigersRecords.length} records — £${tigersRecords.filter(r => r.status === 'Paid' || r.status === 'paid').reduce((s,r) => s+r.amount, 0).toFixed(2)}` },
      { 'Metric': 'Tai Chi', 'Value': `${taiChiRecords.length} records — £${taiChiRecords.filter(r => r.status === 'Paid' || r.status === 'paid').reduce((s,r) => s+r.amount, 0).toFixed(2)}` },
      { 'Metric': 'Private & Remote', 'Value': `${privateRemoteRecords.length} records — £${privateRemoteRecords.filter(r => r.status === 'Paid' || r.status === 'paid').reduce((s,r) => s+r.amount, 0).toFixed(2)}` },
    ];
    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    summaryWs['!cols'] = [{ wch: 35 }, { wch: 40 }];

    // Build workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, summaryWs, 'SUMMARY');
    XLSX.utils.book_append_sheet(wb, buildSheet('Adults', adultsRecords), 'ADULTS');
    XLSX.utils.book_append_sheet(wb, buildSheet('Dragons', dragonsRecords), 'DRAGONS');
    XLSX.utils.book_append_sheet(wb, buildSheet('Tigers', tigersRecords), 'TIGERS');
    XLSX.utils.book_append_sheet(wb, buildSheet('Tai Chi', taiChiRecords), 'TAI CHI');
    XLSX.utils.book_append_sheet(wb, buildSheet('Private & Remote', privateRemoteRecords), 'PRIVATE & REMOTE');

    XLSX.writeFile(wb, `loong-fu-master-report-${selectedYear}.xlsx`);
    toast.success(`Exported ${allSorted.length} records to Excel (${selectedYear})`);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-[#A1A1AA]">Loading financial data...</div></div>;

  const recentExpenses = expenses.filter(e => { const d = new Date(e.date); return !isNaN(d) && d.getFullYear() === selectedYear; }).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 10);

  return (
    <div data-testid="billing-reports" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#D4AF37] uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Billing & Reports 帳單與報告</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">Business Intelligence Dashboard (v4.0)</p>
        </div>
        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-32 bg-white/5 border-white/10 text-white"><Calendar className="w-4 h-4 mr-2 text-[#A1A1AA]" /><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#27272a] border-white/10">
            <SelectItem value="2024" className="text-white">2024</SelectItem>
            <SelectItem value="2025" className="text-white">2025</SelectItem>
            <SelectItem value="2026" className="text-white">2026</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* YTD Summary Cards */}
      <div data-testid="ytd-summary-cards" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-[#18181b] to-[#1a2e1a] border-green-500/40 rounded-sm">
          <CardContent className="p-5 text-center">
            <DollarSign className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-[#A1A1AA] text-xs uppercase tracking-widest">Total Revenue YTD</p>
            <p data-testid="ytd-revenue" className="text-3xl font-bold text-green-400 mt-1">£{financialData.yearToDate.toFixed(0)}</p>
            <p className="text-[#A1A1AA] text-[10px] mt-1">Jan 1 – Today ({selectedYear})</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-[#18181b] to-[#2e1a1a] border-red-500/40 rounded-sm">
          <CardContent className="p-5 text-center">
            <TrendingDown className="w-6 h-6 text-red-400 mx-auto mb-2" />
            <p className="text-[#A1A1AA] text-xs uppercase tracking-widest">Total Expenses YTD</p>
            <p data-testid="ytd-expenses" className="text-3xl font-bold text-red-400 mt-1">£{financialData.yearExpenses.toFixed(0)}</p>
            <p className="text-[#A1A1AA] text-[10px] mt-1">All categories ({selectedYear})</p>
          </CardContent>
        </Card>
        <Card className={`bg-gradient-to-br from-[#18181b] ${financialData.netProfitYTD >= 0 ? 'to-[#1a2e1a] border-emerald-500/40' : 'to-[#2e1a1a] border-red-500/40'} rounded-sm`}>
          <CardContent className="p-5 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2" style={{ color: financialData.netProfitYTD >= 0 ? '#34d399' : '#f87171' }} />
            <p className="text-[#A1A1AA] text-xs uppercase tracking-widest">Net Profit YTD</p>
            <p data-testid="ytd-net-profit" className={`text-3xl font-bold mt-1 ${financialData.netProfitYTD >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              £{financialData.netProfitYTD.toFixed(0)}
            </p>
            <p className="text-[#A1A1AA] text-[10px] mt-1">Revenue − Expenses ({selectedYear})</p>
          </CardContent>
        </Card>
      </div>

      {/* Row A: Category Revenue Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#18181b] border-l-4 border-l-blue-500 border-white/10 rounded-sm"><CardContent className="p-4"><div className="flex items-center gap-3"><Users className="w-5 h-5 text-blue-400" /><div><p className="text-[#A1A1AA] text-xs uppercase">Group Classes</p><p className="text-xl font-bold text-blue-400">£{financialData.categoryTotals.group_class.toFixed(0)}</p></div></div></CardContent></Card>
        <Card className="bg-[#18181b] border-l-4 border-l-green-500 border-white/10 rounded-sm"><CardContent className="p-4"><div className="flex items-center gap-3"><UserCheck className="w-5 h-5 text-green-400" /><div><p className="text-[#A1A1AA] text-xs uppercase">Private Lessons</p><p className="text-xl font-bold text-green-400">£{financialData.categoryTotals.private.toFixed(0)}</p></div></div></CardContent></Card>
        <Card className="bg-[#18181b] border-l-4 border-l-purple-500 border-white/10 rounded-sm cursor-pointer hover:bg-white/5 transition-colors" onClick={() => { setDrilldownCategory("Grading"); setDrilldownTitle("Grading Fees"); setDrilldownOpen(true); }}><CardContent className="p-4"><div className="flex items-center gap-3"><Award className="w-5 h-5 text-purple-400" /><div><p className="text-[#A1A1AA] text-xs uppercase">Grading Fees</p><p data-testid="grading-collected" className="text-xl font-bold text-purple-400">£{(categorySums?.Grading?.collected || 0).toFixed(0)}</p></div></div></CardContent></Card>
        <Card className="bg-[#18181b] border-l-4 border-l-orange-500 border-white/10 rounded-sm cursor-pointer hover:bg-white/5 transition-colors" onClick={() => { setDrilldownCategory("Membership"); setDrilldownTitle("Membership & Insurance"); setDrilldownOpen(true); }}><CardContent className="p-4"><div className="flex items-center gap-3"><Shield className="w-5 h-5 text-orange-400" /><div><p className="text-[#A1A1AA] text-xs uppercase">Membership & Insurance</p><p data-testid="membership-collected" className="text-xl font-bold text-orange-400">£{(categorySums?.Membership?.collected || 0).toFixed(0)}</p></div></div></CardContent></Card>
      </div>

      {/* Consolidated: Stats + Gauge */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[#18181b] border-white/10 rounded-sm">
          <CardContent className="p-4 text-center">
            <Users className="w-5 h-5 text-white mx-auto mb-1" />
            <p className="text-[#A1A1AA] text-xs uppercase">Total Students</p>
            <p className="text-2xl font-bold text-white mt-1">{students.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-white/10 rounded-sm">
          <CardContent className="p-4 text-center">
            <Activity className="w-5 h-5 text-[#22c55e] mx-auto mb-1" />
            <p className="text-[#A1A1AA] text-xs uppercase">Attendance ({selectedYear})</p>
            <p className="text-2xl font-bold text-white mt-1">{attendance.filter(a => { let d; if (a.date && a.date.includes('/')) { const p = a.date.split('/'); d = new Date(`${p[2]}-${p[1]}-${p[0]}`); } else { d = new Date(a.date || a.checked_in_at); } return !isNaN(d) && d.getFullYear() === selectedYear; }).length}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-red-500/30 rounded-sm">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <p className="text-[#A1A1AA] text-xs uppercase">Outstanding</p>
            <p className="text-2xl font-bold text-red-400 mt-1">£{financialData.outstanding.toFixed(0)}</p>
          </CardContent>
        </Card>
        <Card data-testid="revenue-health-gauge" className="bg-[#18181b] border-white/10 rounded-sm">
          <CardContent className="p-4 flex flex-col items-center justify-center">
            <p className="text-[#A1A1AA] text-xs uppercase tracking-wide mb-1">Revenue Health</p>
            <RevenueGauge value={financialData.monthlyTotal} target={monthlyTarget} />
            <div data-testid="target-setting" className="flex items-center gap-2 mt-1">
              <Settings className="w-3 h-3 text-[#A1A1AA]" />
              <span className="text-[#A1A1AA] text-[10px]">Target £</span>
              <input
                data-testid="monthly-target-input"
                type="number"
                min="100"
                step="50"
                value={monthlyTarget}
                onChange={e => {
                  const raw = e.target.value;
                  const num = parseFloat(raw);
                  if (raw === '') { setMonthlyTarget(''); return; }
                  if (isFinite(num) && num > 0) {
                    setMonthlyTarget(num);
                    try { localStorage.setItem('loongfu_monthly_target', String(num)); } catch {}
                    axios.put(`${API}/settings/monthly_target`, { value: num }).catch(() => {});
                  }
                }}
                className="w-16 bg-white/5 border border-white/10 rounded text-white text-xs text-center px-1 py-0.5 focus:outline-none focus:border-[#D4AF37]/50"
                onBlur={() => {
                  if (!monthlyTarget || monthlyTarget <= 0) {
                    setMonthlyTarget(DEFAULT_TARGET);
                    try { localStorage.setItem('loongfu_monthly_target', String(DEFAULT_TARGET)); } catch {}
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income Mix */}
        <Card className="bg-[#18181b] border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="text-base font-semibold text-white uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <PieChartIcon className="w-5 h-5 text-[#D4AF37]" />Income Mix ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {financialData.pieData.length > 0 ? (
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-48 h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={financialData.pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">
                      {financialData.pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie><Tooltip formatter={(v) => `£${v.toFixed(0)}`} contentStyle={{ background: '#222', border: '1px solid #555', borderRadius: '6px', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} /></PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-col gap-3">
                  {financialData.pieData.map((entry, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: entry.color }} />
                      <div><p className="text-white text-sm">{entry.name}</p><p className="text-[#A1A1AA] text-xs">£{entry.value.toFixed(0)} ({((entry.value / financialData.totalRevenue) * 100).toFixed(1)}%)</p></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : <div className="flex items-center justify-center h-48 text-[#A1A1AA]">No revenue data for {selectedYear}</div>}
          </CardContent>
        </Card>
        {/* Attendance vs Revenue (swapped here) */}
        <Card data-testid="attendance-vs-revenue" className="bg-[#18181b] border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="text-base font-semibold text-white uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <Activity className="w-5 h-5 text-[#D4AF37]" />Attendance vs Revenue ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={financialData.attendanceVsRevenue} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#A1A1AA', fontSize: 12 }} />
                  <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: '#A1A1AA', fontSize: 12 }} tickFormatter={(v) => `£${v}`} />
                  <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fill: '#A1A1AA', fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: '#222', border: '1px solid #555', borderRadius: '6px', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} formatter={(value, name) => name === "revenue" ? [`£${value.toFixed(0)}`, "Revenue"] : [value, "Attendance"]} />
                  <Bar yAxisId="left" dataKey="revenue" fill="#D4AF37" opacity={0.7} radius={[4, 4, 0, 0]} name="revenue" />
                  <Line yAxisId="right" type="monotone" dataKey="attendance" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e', r: 4 }} name="attendance" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4 pt-4 border-t border-white/5">
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-[#D4AF37]" /><span className="text-[#A1A1AA] text-xs">Revenue (£)</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#22c55e]" /><span className="text-[#A1A1AA] text-xs">Attendance</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Performance (full width, swapped from above) */}
      <Card className="bg-[#18181b] border-white/10 rounded-sm">
        <CardHeader className="border-b border-white/5 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-white uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <BarChart3 className="w-5 h-5 text-[#D4AF37]" />Performance
            </CardTitle>
            <Select data-testid="performance-toggle" value={performanceMonths} onValueChange={setPerformanceMonths}>
              <SelectTrigger className="w-28 h-8 bg-white/5 border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#27272a] border-white/10">
                <SelectItem value="6" className="text-white">6 Months</SelectItem>
                <SelectItem value="12" className="text-white">12 Months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={financialData.lastNMonths} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#A1A1AA', fontSize: 11 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#A1A1AA', fontSize: 12 }} tickFormatter={(v) => `£${v}`} />
                <Tooltip formatter={(v, name) => [`£${v.toFixed(0)}`, CATEGORY_LABELS[name] || name]} contentStyle={{ background: '#222', border: '1px solid #555', borderRadius: '6px', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                <Bar dataKey="group_class" stackId="a" fill={CATEGORY_COLORS.group_class} />
                <Bar dataKey="private" stackId="a" fill={CATEGORY_COLORS.private} />
                <Bar dataKey="grading" stackId="a" fill={CATEGORY_COLORS.grading} />
                <Bar dataKey="membership" stackId="a" fill={CATEGORY_COLORS.membership} />
                <Bar dataKey="tai_chi" stackId="a" fill={CATEGORY_COLORS.tai_chi} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mt-4 pt-4 border-t border-white/5">
            {Object.entries(CATEGORY_COLORS).map(([key, color]) => (
              <div key={key} className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} /><span className="text-[#A1A1AA] text-xs">{CATEGORY_LABELS[key].split(' ')[0]}</span></div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Row 3b: Income vs Expenses + Expense Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income vs Expenses Grouped Bar Chart */}
        <Card data-testid="income-vs-expense-chart" className="bg-[#18181b] border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/5 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-white uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <BarChart3 className="w-5 h-5 text-[#D4AF37]" />Income vs Expenses
              </CardTitle>
              <Select data-testid="inc-exp-toggle" value={incExpMonths} onValueChange={setIncExpMonths}>
                <SelectTrigger className="w-28 h-8 bg-white/5 border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#27272a] border-white/10">
                  <SelectItem value="6" className="text-white">6 Months</SelectItem>
                  <SelectItem value="12" className="text-white">12 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={financialData.incomeVsExpenseData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#A1A1AA', fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#A1A1AA', fontSize: 12 }} tickFormatter={(v) => `£${v}`} />
                  <Tooltip formatter={(v) => `£${v.toFixed(0)}`} contentStyle={{ background: '#222', border: '1px solid #555', borderRadius: '6px', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                  <Bar dataKey="Income" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4 pt-4 border-t border-white/5">
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-[#22c55e]" /><span className="text-[#A1A1AA] text-xs">Income</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-[#ef4444]" /><span className="text-[#A1A1AA] text-xs">Expenses</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Expense Distribution Pie */}
        <Card data-testid="expense-distribution-pie" className="bg-[#18181b] border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="text-base font-semibold text-white uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <PieChartIcon className="w-5 h-5 text-red-400" />Expense Distribution ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {financialData.expenseDistribution.length > 0 ? (
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-48 h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={financialData.expenseDistribution} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">
                        {financialData.expenseDistribution.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip formatter={(v) => `£${v.toFixed(0)}`} contentStyle={{ background: '#222', border: '1px solid #555', borderRadius: '6px', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-col gap-3">
                  {financialData.expenseDistribution.map((entry, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: entry.color }} />
                      <div>
                        <p className="text-white text-sm">{entry.name}</p>
                        <p className="text-[#A1A1AA] text-xs">£{entry.value.toFixed(0)} ({((entry.value / financialData.yearExpenses) * 100).toFixed(1)}%)</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : <div className="flex items-center justify-center h-48 text-[#A1A1AA]">No expenses logged for {selectedYear}</div>}
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Expenses Section */}
      <Card data-testid="expenses-section" className="bg-[#18181b] border-white/10 rounded-sm">
        <CardHeader className="border-b border-white/5 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-white uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <TrendingDown className="w-5 h-5 text-red-400" />Expenses ({selectedYear})
            </CardTitle>
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#A1A1AA]">YTD: <span className="text-red-400 font-semibold">£{financialData.yearExpenses.toFixed(0)}</span></span>
              <Button data-testid="log-expense-btn" size="sm" onClick={() => setShowExpenseForm(!showExpenseForm)} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30">
                <Plus className="w-4 h-4 mr-1" />Log Expense
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {/* Expense Form */}
          {showExpenseForm && (
            <div data-testid="expense-form" className="p-4 mb-4 bg-white/5 rounded-sm border border-white/10 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Category</Label>
                  <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#27272a] border-white/10">
                      {EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Amount (£)</Label>
                  <Input data-testid="expense-amount" type="number" step="0.01" min="0" value={expenseForm.amount} onChange={e => setExpenseForm(p => ({ ...p, amount: e.target.value }))} className="bg-white/5 border-white/10 text-white" placeholder="0.00" />
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Date</Label>
                  <Input data-testid="expense-date" type="date" value={expenseForm.date} onChange={e => setExpenseForm(p => ({ ...p, date: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Description</Label>
                  <Input data-testid="expense-desc" value={expenseForm.description} onChange={e => setExpenseForm(p => ({ ...p, description: e.target.value }))} className="bg-white/5 border-white/10 text-white" placeholder="Optional note" />
                </div>
                <div className="flex items-end">
                  <Button data-testid="save-expense-btn" onClick={handleAddExpense} disabled={savingExpense} className="w-full bg-red-600 hover:bg-red-700 text-white">
                    {savingExpense ? "Saving..." : "Save Expense"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {/* Recent Expenses List */}
          {recentExpenses.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {recentExpenses.map((exp) => (
                <div key={exp.id} className="flex items-center justify-between p-3 bg-white/5 rounded-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center bg-red-500/20"><Minus className="w-4 h-4 text-red-400" /></div>
                    <div>
                      <p className="text-white text-sm font-medium">{exp.category}</p>
                      <p className="text-[#A1A1AA] text-xs">{exp.date}{exp.description ? ` — ${exp.description}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-red-400 font-semibold">-£{parseFloat(exp.amount).toFixed(0)}</p>
                    <button data-testid={`delete-expense-${exp.id}`} onClick={() => handleDeleteExpense(exp.id)} className="text-[#A1A1AA] hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-[#A1A1AA] text-sm text-center py-4">No expenses logged for {selectedYear}</p>}
        </CardContent>
      </Card>

      {/* Outstanding */}
      {financialData.outstandingRecords.length > 0 && (
        <Card className="bg-[#18181b] border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="text-base font-semibold text-white uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}><AlertTriangle className="w-5 h-5 text-red-400" />Outstanding Payments ({financialData.outstandingRecords.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {financialData.outstandingRecords.map((rec, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-sm">
                  <div className="flex items-center gap-3"><StudentAvatar name={rec.name} /><div><p className="text-white text-sm font-medium">{rec.name}</p><p className="text-[#A1A1AA] text-xs">{rec.type}</p></div></div>
                  <p className="text-red-400 font-semibold">£{rec.amount.toFixed(0)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* CSV Export */}
      <Card className="bg-gradient-to-r from-[#18181b] to-[#1a1a2e] border-[#D4AF37]/30 rounded-sm">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#D4AF37]/20 rounded-full"><FileSpreadsheet className="w-8 h-8 text-[#D4AF37]" /></div>
              <div>
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>Master Financial Export</h3>
                <p className="text-[#A1A1AA] text-sm">Download all {financialData.allRecords.length} payment records as CSV</p>
              </div>
            </div>
            <Button onClick={exportMasterXLSX} className="bg-[#D4AF37] hover:bg-[#b8962e] text-black font-semibold px-6 py-3 h-auto" data-testid="export-master-xlsx-btn">
              <Download className="w-5 h-5 mr-2" />Download Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* v12.7.2: Category Drill-Down Dialog */}
      <Dialog open={drilldownOpen} onOpenChange={setDrilldownOpen}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-wide text-[#D4AF37]" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {drilldownTitle} — All Records
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            {(() => {
              // v12.8.0: Show records from BOTH tables — unified data has _source tag
              const categoryPayments = groupPayments.filter(p => {
                const cat = p.category || 'Lesson';
                return cat === drilldownCategory && (p.status === 'paid' || p._legacy);
              });
              const total = categoryPayments.reduce((s, p) => s + (p.amount || 0), 0);
              const getStudName = (id) => students.find(s => s.id === id)?.name || 'Unknown';
              
              const handleDrilldownDelete = async (payment) => {
                if (!window.confirm("Delete this payment record?")) return;
                try {
                  const endpoint = payment._source === 'fee_payments' 
                    ? `${API}/fee-payments/${payment.id}` 
                    : `${API}/payments/${payment.id}`;
                  await axios.delete(endpoint);
                  toast.success("Payment deleted — totals refreshed");
                  const [paymentsRes, catRes] = await Promise.all([
                    axios.get(`${API}/payments/unified`).catch(() => ({ data: [] })),
                    axios.get(`${API}/payments/category-sums`).catch(() => ({ data: {} })),
                  ]);
                  setGroupPayments(paymentsRes.data || []);
                  setCategorySums(catRes.data || {});
                } catch (err) {
                  toast.error("Failed to delete");
                }
              };
              
              return (
                <>
                  <div className="flex justify-between items-center mb-3 px-1">
                    <span className="text-[#A1A1AA] text-sm">{categoryPayments.length} record{categoryPayments.length !== 1 ? 's' : ''}</span>
                    <span className="text-white font-bold text-lg">Total: £{total.toFixed(2)}</span>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5">
                        <TableHead className="text-[#A1A1AA] text-xs uppercase">Student</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase">Amount</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase">Date</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase">Source</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase text-right">Delete</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryPayments.length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#A1A1AA]">No records found</TableCell></TableRow>
                      ) : categoryPayments.map(p => (
                        <TableRow key={`${p._source}-${p.id}`} className="border-white/5 hover:bg-white/5">
                          <TableCell className="text-white font-medium">{getStudName(p.student_id)}</TableCell>
                          <TableCell className="text-white font-mono">£{(p.amount || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-[#A1A1AA] text-xs">{p.payment_date || p.date || '—'}</TableCell>
                          <TableCell className="text-xs">
                            {p._legacy 
                              ? <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 font-bold text-[9px] uppercase">Legacy</span>
                              : <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 font-bold text-[9px] uppercase">Current</span>
                            }
                          </TableCell>
                          <TableCell className="text-right">
                            <Button data-testid={`drilldown-delete-${p.id}`} size="sm" variant="ghost" onClick={() => handleDrilldownDelete(p)} className="text-[#ef4444] hover:bg-red-500/10 h-7 w-7 p-0">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
